import React, { useEffect, useState } from 'react';
import './SessionAnalytics.css';
import { Box, Typography, Grid } from '@mui/material';
import AnalyticsCard from '../AnalyticsCard/AnalyticsCard';
import SessionLineChart from './SessionLineChart/SessionLineChart';
import DonutChart from '../DonutChart/DonutChart';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { CardSkeleton, DonutSkeleton } from '../../SkeletonLoader/SkeletonLoader';
import '../../SkeletonLoader/SkeletonLoader.css';
import GroupSessionIcon from '../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import OneOnOneSessionIcon from '../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import PrimeSessionIcon from '../../../../assets/Admin/SessionIcons/prime-session-icon.svg';

const SessionAnalytics = ({ timePeriod = 'last_month' }) => {
  const [sessionMetricsData, setSessionMetricsData] = useState(null);
  const [cancellationMetricsData, setCancellationMetricsData] = useState(null);
  const [repeatSessionData, setRepeatSessionData] = useState(null);
  const [subjectDemandData, setSubjectDemandData] = useState(null);
  const [sessionAnalyticsData, setSessionAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchAnalytics = async () => {
      try {
        setLoading(true);

        // Fetch all APIs in parallel
        const [
          sessionMetricsResponse,
          cancellationMetricsResponse,
          repeatSessionResponse,
          subjectDemandResponse,
          sessionAnalyticsResponse,
        ] = await Promise.all([
          axiosInstance.get(
            `/admin/dashboard/session-metrics?period=${mapTimePeriodForNewAPIs(timePeriod)}`
          ),
          axiosInstance.get(
            `/admin/dashboard/session-cancellation-metrics?period=${mapTimePeriodForNewAPIs(timePeriod)}`
          ),
          axiosInstance.get(
            `/admin/dashboard/repeat-session-metrics?period=${mapTimePeriodForNewAPIs(timePeriod)}`
          ),
          axiosInstance.get(
            `/admin/dashboard/subject-demand-analytics?period=${mapTimePeriodForSubjectDemand(timePeriod)}`
          ),
          axiosInstance.get('/admin/dashboard/session-analytics'),
        ]);

        if (isMounted) {
          setSessionMetricsData(sessionMetricsResponse.data);
          setCancellationMetricsData(cancellationMetricsResponse.data);
          setRepeatSessionData(repeatSessionResponse.data);
          setSubjectDemandData(subjectDemandResponse.data);
          setSessionAnalyticsData(sessionAnalyticsResponse.data);
        }
      } catch (error) {
        if (isMounted) {
          setSessionMetricsData(null);
          setCancellationMetricsData(null);
          setRepeatSessionData(null);
          setSubjectDemandData(null);
          setSessionAnalyticsData(null);
        }
        console.error('Error fetching analytics data:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchAnalytics();
    return () => {
      isMounted = false;
    };
  }, [timePeriod]);

  // Prepare data for cards from new APIs
  const totalSessions = sessionMetricsData?.total_sessions?.value ?? 0;
  const totalSessionsGrowth = sessionMetricsData?.total_sessions?.growth_percentage ?? 0;

  const bookingRatio = sessionMetricsData?.booking_ratio?.value ?? 0;
  const bookingRatioGrowth = sessionMetricsData?.booking_ratio?.growth_percentage ?? 0;

  const avgBookingTime = sessionMetricsData?.avg_booking_time?.value ?? 0;
  const avgBookingTimeGrowth = sessionMetricsData?.avg_booking_time?.growth_percentage ?? 0;

  const completionRate = sessionMetricsData?.completion_rate?.value ?? 0;
  const completionRateGrowth = sessionMetricsData?.completion_rate?.growth_percentage ?? 0;

  // Repeat session rate from new API
  const repeatSessionRate = repeatSessionData?.repeat_session_rate?.value ?? 0;
  const repeatSessionRateGrowth = repeatSessionData?.repeat_session_rate?.growth_percentage ?? 0;

  // Session type data from session analytics API
  const sessionTypeData = [
    { label: 'One-On-One', value: sessionAnalyticsData?.by_type?.one_on_one ?? 0 },
    { label: 'Group', value: sessionAnalyticsData?.by_type?.group ?? 0 },
    { label: 'Prime', value: sessionAnalyticsData?.by_type?.prime ?? 0 },
  ];
  const donutTotal = sessionTypeData.reduce((sum, item) => sum + item.value, 0);

  const sessionLineChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    dataPoints: [
      { month: 'Academic', value: 0 }, // Placeholder - not in new APIs
      { month: 'Creative', value: 0 }, // Placeholder - not in new APIs
    ],
    highlights: [
      { month: 'Apr', value: '$1,000' },
      { month: 'Jul', value: '$9,000' },
    ],
    maxValue: '20K',
  };

  // Cancellation data from new API
  const sessionCancellation = {
    overall: cancellationMetricsData?.cancellation_rate?.overall ?? 0,
    byStudent: cancellationMetricsData?.cancellation_rate?.by_learner ?? 0,
    byTeacher: cancellationMetricsData?.cancellation_rate?.by_instructor ?? 0,
    byType: [
      {
        label: 'One-On-One',
        value: cancellationMetricsData?.cancelled_sessions?.one_on_one ?? 0,
      },
      {
        label: 'Group',
        value: cancellationMetricsData?.cancelled_sessions?.group ?? 0,
      },
      {
        label: 'Prime',
        value: cancellationMetricsData?.cancelled_sessions?.prime ?? 0,
      },
    ],
  };

  // Process cancellation reasons from new API
  const processCancellationReasons = reasons => {
    if (!reasons || reasons.length === 0) {
      return [{ label: 'No cancellation data available', value: 0 }];
    }

    return reasons.map((reason, index) => ({
      label: reason.reason || `Reason ${index + 1}`,
      value: reason.count || 0,
    }));
  };

  const cancellationReasons = processCancellationReasons(
    cancellationMetricsData?.major_cancellation_reasons
  );

  // Only show top 5 cancellation reasons by value (descending)
  const topCancellationReasons =
    cancellationReasons.length > 1 &&
    cancellationReasons[0].label !== 'No cancellation data available'
      ? [...cancellationReasons].sort((a, b) => b.value - a.value).slice(0, 5)
      : cancellationReasons;

  // Get appropriate comparison period text for the selected time period
  const getComparisonPeriodText = () => {
    switch (timePeriod) {
      case 'last_day':
        return 'previous day';
      case 'last_week':
        return 'previous week';
      case 'last_2_weeks':
        return 'last 2 weeks';
      case 'last_month':
        return 'last month';
      case 'last_3_months':
        return 'last 3 months';
      case 'last_6_months':
        return 'last 6 months';
      case 'last_year':
        return 'previous year';
      default:
        return 'last month';
    }
  };

  // Custom card for listing cancellation reasons (merged)
  const CancellationReasonsCard = ({ data }) => (
    <div className="duration-list-card">
      <div className="duration-list-title">Major Cancellation Reasons</div>
      <div className="duration-list-content">
        {data.map(item => (
          <div className="duration-list-item" key={item.label}>
            <span className="duration-list-label">{item.label}:</span>
            <span className="duration-list-value">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
  // Custom card for listing cancelled sessions by type (merged)
  const CancelledSessionCard = ({ data }) => (
    <div className="duration-list-card">
      <div className="duration-list-title">Cancelled Session:</div>
      <div className="duration-list-content">
        {data.map(item => (
          <div className="duration-list-item" key={item.label}>
            <span className="duration-list-label">{item.label}:</span>
            <span className="duration-list-value">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // Custom card for merged session cancellation rates
  const SessionCancellationRateCard = ({ overall, byStudent, byTeacher }) => (
    <div className="duration-list-card">
      <div className="duration-list-title">Session Cancellation Rate</div>
      <div className="duration-list-content">
        <div className="duration-list-item">
          <span className="duration-list-label">Overall:</span>
          <span className="duration-list-value">{overall}%</span>
        </div>
        <div className="duration-list-item">
          <span className="duration-list-label">By Learner:</span>
          <span className="duration-list-value">{byStudent}%</span>
        </div>
        <div className="duration-list-item">
          <span className="duration-list-label">By Instructor:</span>
          <span className="duration-list-value">{byTeacher}%</span>
        </div>
      </div>
    </div>
  );

  // Skeleton loader for subject demand table
  const SubjectDemandTableSkeleton = () => (
    <div className="subject-demand-table-container">
      <table className="subject-demand-table">
        <thead>
          <tr>
            <th className="subject-demand-table-header">Subject</th>
            <th className="subject-demand-table-header">Bookings</th>
            <th className="subject-demand-table-header">Revenue</th>
            <th className="subject-demand-table-header">Growth</th>
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, index) => (
            <tr key={index} className="subject-demand-table-row">
              <td className="subject-demand-table-cell subject-demand-table-cell-subject">
                <div className="skeleton-text" style={{ height: '12px', width: '100%' }} />
              </td>
              <td className="subject-demand-table-cell subject-demand-table-cell-count">
                <div
                  className="skeleton-text"
                  style={{ height: '12px', width: '60%', margin: '0 auto' }}
                />
              </td>
              <td className="subject-demand-table-cell subject-demand-table-cell-revenue">
                <div
                  className="skeleton-text"
                  style={{ height: '12px', width: '70%', marginLeft: 'auto' }}
                />
              </td>
              <td className="subject-demand-table-cell subject-demand-table-cell-growth">
                <div
                  className="skeleton-text"
                  style={{ height: '12px', width: '50%', margin: '0 auto' }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Helper function to render subject demand table
  const renderSubjectDemandTable = subjects => {
    if (!subjects || subjects.length === 0) {
      return <div className="subject-demand-empty-state">No subjects available</div>;
    }

    return (
      <div className="subject-demand-table-container">
        <table className="subject-demand-table">
          <thead>
            <tr>
              <th className="subject-demand-table-header">Subject</th>
              <th className="subject-demand-table-header">Bookings</th>
              <th className="subject-demand-table-header">Revenue</th>
              <th className="subject-demand-table-header">Growth</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((subject, index) => (
              <tr key={subject.id || index} className="subject-demand-table-row">
                <td className="subject-demand-table-cell subject-demand-table-cell-subject">
                  {subject.title}
                </td>
                <td className="subject-demand-table-cell subject-demand-table-cell-count">
                  {subject.booking_count}
                </td>
                <td className="subject-demand-table-cell subject-demand-table-cell-revenue">
                  QR {subject.total_revenue?.toFixed(2) || '0.00'}
                </td>
                <td className="subject-demand-table-cell subject-demand-table-cell-growth">
                  {subject.growth_rate?.toFixed(1) || '0.0'}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper function to map time period to subject demand API format
  const mapTimePeriodForSubjectDemand = period => {
    switch (period) {
      case 'last_day':
        return 'today';
      case 'last_week':
        return '1_week';
      case 'last_2_weeks':
        return '2_weeks';
      case 'last_month':
        return '1_month';
      case 'last_3_months':
        return '3_months';
      case 'last_6_months':
        return '6_months';
      case 'last_year':
        return '1_year';
      default:
        return '1_month';
    }
  };

  // Helper function to map time period for new APIs
  const mapTimePeriodForNewAPIs = period => {
    switch (period) {
      case 'last_day':
        return 'today';
      case 'last_week':
        return '1_week';
      case 'last_2_weeks':
        return '2_weeks';
      case 'last_month':
        return '1_month';
      case 'last_3_months':
        return '3_months';
      case 'last_6_months':
        return '6_months';
      case 'last_year':
        return '1_year';
      default:
        return '1_month';
    }
  };

  return (
    <Box className="session-analytics-section">
      <Typography className="session-analytics-title">Session Analytics</Typography>
      <Grid container spacing={2} className="session-analytics-cards">
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Total Sessions"
              value={totalSessions}
              percentage={totalSessionsGrowth}
              isPositive={totalSessionsGrowth >= 0}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Booking Ratio"
              value={bookingRatio}
              percentage={bookingRatioGrowth}
              isPositive={bookingRatioGrowth >= 0}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Avg. Booking Time"
              value={`${avgBookingTime} hrs`}
              percentage={avgBookingTimeGrowth}
              isPositive={avgBookingTimeGrowth >= 0}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Completion Rate"
              value={completionRate}
              percentage={completionRateGrowth}
              isPositive={completionRateGrowth >= 0}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Repeat Session Rate"
              value={`${repeatSessionRate}%`}
              percentage={repeatSessionRateGrowth}
              isPositive={repeatSessionRateGrowth >= 0}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <SessionCancellationRateCard
              overall={sessionCancellation.overall}
              byStudent={sessionCancellation.byStudent}
              byTeacher={sessionCancellation.byTeacher}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          {loading ? <CardSkeleton /> : <CancelledSessionCard data={sessionCancellation.byType} />}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {loading ? <CardSkeleton /> : <CancellationReasonsCard data={topCancellationReasons} />}
        </Grid>
      </Grid>

      <Grid container spacing={2} className="subject-session-container">
        <Grid item xs={12} md={8}>
          <Box className="subject-demand-container">
            <Typography className="section-title">Subject Demand</Typography>
            <Box className="subject-grid">
              <Box className="subject-card">
                <Typography className="subject-title">Highest Subject Demand</Typography>
                {loading ? (
                  <SubjectDemandTableSkeleton />
                ) : (
                  renderSubjectDemandTable(subjectDemandData?.highest_demand?.subjects)
                )}
              </Box>
              <Box className="subject-card">
                <Typography className="subject-title">Lowest Subject Demand</Typography>
                {loading ? (
                  <SubjectDemandTableSkeleton />
                ) : (
                  renderSubjectDemandTable(subjectDemandData?.lowest_demand?.subjects)
                )}
              </Box>
              <Box className="subject-card">
                <Typography className="subject-title">Fastest Growing Subject</Typography>
                {loading ? (
                  <SubjectDemandTableSkeleton />
                ) : (
                  renderSubjectDemandTable(subjectDemandData?.fastest_growing?.subjects)
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          {loading ? (
            <DonutSkeleton />
          ) : (
            <DonutChart
              title="Session Type"
              centerValue={donutTotal}
              data={sessionTypeData}
              colors={['#2563EB', '#7BCB8F', '#FFCA28']}
              icons={[OneOnOneSessionIcon, GroupSessionIcon, PrimeSessionIcon]}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} className="analytics-charts">
        <Grid item xs={12}>
          <SessionLineChart
            title="Session"
            data={sessionLineChartData}
            legendItems={[
              { color: '#2563EB', label: 'Academic' },
              { color: '#ABC4FF', label: 'Creative' },
            ]}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SessionAnalytics;
